import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

class MixAndMatchProductCard extends HTMLElement {
  constructor() {
    super();
    this.quantity = 0;
  }

  connectedCallback() {
    if (this.dataset.disabled === 'true') return;

    this.addButton = this.querySelector('[data-ref="add-button"]');
    this.qtyControls = this.querySelector('[data-ref="qty-controls"]');
    this.qtyInput = this.querySelector('[data-ref="mix-match-qty-input"]');
    this.qtyBadge = this.querySelector('[data-ref="qty-badge"]');
    this.variantSelect = this.querySelector('[data-ref="variant-select"]');
    this.productImage = this.querySelector('[data-ref="product-image"]');
    this.variantTitleEl = this.querySelector('[data-ref="variant-title"]');
    this.priceDisplay = this.querySelector('[data-ref="price-display"]');

    this.addButton?.addEventListener('click', this.#handleAdd);
    this.qtyInput?.addEventListener('change', this.#handleQuantityChange);
    this.variantSelect?.addEventListener('change', this.#handleVariantChange);

    this.#updateVariantTitleDisplay();
    this.#updatePriceDisplay();
  }

  disconnectedCallback() {
    this.addButton?.removeEventListener('click', this.#handleAdd);
    this.qtyInput?.removeEventListener('change', this.#handleQuantityChange);
    this.variantSelect?.removeEventListener('change', this.#handleVariantChange);
  }

  #handleAdd = () => {
    const quantity = parseInt(this.qtyInput?.step, 10) || 1;

    this.#applyQuantityState(quantity);
    this.#syncQuantitySelector(quantity);
    this.dispatchSelectionChange();
  };

  #handleQuantityChange = () => {
    if (this._syncingQuantity) return;

    const quantity = Math.max(0, parseInt(this.qtyInput?.value, 10) || 0);
    this.#applyQuantityState(quantity);

    if (quantity < 1) {
      this.#resetQuantityInput();
    }

    this.dispatchSelectionChange();
  };

  #handleVariantChange = () => {
    const option = this.variantSelect.options[this.variantSelect.selectedIndex];
    if (!option) return;

    this.dataset.variantId = option.value;
    this.dataset.price = option.dataset.price;
    this.dataset.compareAtPrice = option.dataset.compareAtPrice || option.dataset.price;
    this.dataset.variantTitle = option.dataset.title || option.textContent.trim();

    if (option.dataset.image && this.productImage) {
      this.productImage.src = option.dataset.image;
    }

    this.#updateVariantTitleDisplay();
    this.#updatePriceDisplay();
    this.dispatchSelectionChange();
  };

  #updateVariantTitleDisplay() {
    if (!this.variantTitleEl) return;

    const variantTitle = this.getVariantTitle();

    if (variantTitle && variantTitle !== 'Default Title') {
      this.variantTitleEl.textContent = ` — ${variantTitle}`;
      this.variantTitleEl.hidden = false;
    } else {
      this.variantTitleEl.textContent = '';
      this.variantTitleEl.hidden = true;
    }
  }

  #updatePriceDisplay() {
    if (!this.priceDisplay) return;

    const price = parseInt(this.dataset.price, 10);
    const compareAtPrice = parseInt(this.dataset.compareAtPrice, 10);
    const priceEl = this.priceDisplay.querySelector('[data-ref="price"]');
    const compareEl = this.priceDisplay.querySelector('[data-ref="compare-at-price"]');

    if (priceEl && !Number.isNaN(price)) {
      priceEl.innerHTML = formatMoney(price);
      priceEl.dataset.value = String(price);
    }

    if (compareEl) {
      if (!Number.isNaN(compareAtPrice) && compareAtPrice > price) {
        compareEl.innerHTML = formatMoney(compareAtPrice);
        compareEl.dataset.value = String(compareAtPrice);
      } else {
        compareEl.innerHTML = '';
        compareEl.dataset.value = String(compareAtPrice || price);
      }
    }
  }

  getVariantTitle() {
    if (this.variantSelect) {
      const option = this.variantSelect.options[this.variantSelect.selectedIndex];
      return option?.dataset.title || option?.textContent?.trim() || '';
    }

    return this.dataset.variantTitle || '';
  }

  #applyQuantityState(quantity) {
    this.quantity = quantity;
    this.classList.toggle('mix-match-card--selected', quantity > 0);

    if (this.qtyBadge) {
      this.qtyBadge.textContent = String(quantity);
      this.qtyBadge.classList.toggle('hidden', quantity === 0);
    }

    if (this.addButton && this.qtyControls) {
      const showSelector = quantity > 0;
      this.addButton.classList.toggle('hidden', showSelector);
      this.qtyControls.classList.toggle('hidden', !showSelector);
    }
  }

  #resetQuantityInput() {
    if (!this.qtyInput) return;

    this._syncingQuantity = true;
    this.qtyInput.value = '0';
    this._syncingQuantity = false;
  }

  #syncQuantitySelector(quantity) {
    const quantitySelector = this.querySelector('quantity-selector');
    const input = quantitySelector?.querySelector('input[type="number"]');
    if (!input || (parseInt(input.value, 10) || 0) === quantity) return;

    if (quantity < 1) {
      this.#resetQuantityInput();
      return;
    }

    this._syncingQuantity = true;
    quantitySelector.setQuantity(quantity);
    this._syncingQuantity = false;
  }

  setQuantity(quantity) {
    this.#applyQuantityState(quantity);
    this.#syncQuantitySelector(quantity);
  }

  getSelectionData() {
    if (this.quantity <= 0 || this.dataset.available !== 'true') return null;

    return {
      productId: this.dataset.productId,
      productHandle: this.dataset.productHandle,
      productTitle: this.dataset.productTitle,
      variantId: this.dataset.variantId,
      variantTitle: this.getVariantTitle(),
      quantity: this.quantity,
      price: parseInt(this.dataset.price, 10),
      compareAtPrice: parseInt(this.dataset.compareAtPrice, 10),
      image: this.dataset.image,
    };
  }

  dispatchSelectionChange() {
    this.dispatchEvent(
      new CustomEvent('mix-match:selection-change', {
        bubbles: true,
        composed: true,
        detail: { card: this },
      }),
    );

    const parent = this.closest('mix-and-match-component');
    parent?.onSelectionChange?.();
  }

  clearSelection() {
    this.setQuantity(0);
    this.closest('mix-and-match-component')?.onSelectionChange?.();
  }

  restoreFromState({ variantId, quantity }) {
    if (this.dataset.disabled === 'true' || this.dataset.available !== 'true') return false;

    const qty = parseInt(quantity, 10);
    if (!qty || qty < 1) return false;

    if (this.variantSelect && variantId) {
      const option = Array.from(this.variantSelect.options).find((opt) => opt.value === String(variantId));
      if (!option) return false;

      this.variantSelect.value = option.value;
      this.dataset.variantId = option.value;
      this.dataset.price = option.dataset.price;
      this.dataset.compareAtPrice = option.dataset.compareAtPrice || option.dataset.price;
      this.dataset.variantTitle = option.dataset.title || option.textContent.trim();

      if (option.dataset.image && this.productImage) {
        this.productImage.src = option.dataset.image;
      }

      this.#updateVariantTitleDisplay();
      this.#updatePriceDisplay();
    } else if (variantId && String(variantId) !== String(this.dataset.variantId)) {
      return false;
    }

    this.setQuantity(qty);
    return true;
  }
}

class MixAndMatchComponent extends HTMLElement {
  constructor() {
    super();
    this.selections = new Map();
    this.tiers = [];
    this.maxTierQuantity = 0;
    this.minProducts = 2;
    this.isSubmitting = false;
    this.isRestoring = false;
    this._groupNavGroups = [];
    this._groupNavScrollHandler = null;
    this._groupNavScrollRaf = null;
    this._groupNavResizeHandler = null;
  }

  connectedCallback() {
    this.parseConfig();

    this.summaryCount = this.querySelector('[data-ref="summary-count"]');
    this.summaryPanel = this.querySelector('[data-ref="summary-panel"]');
    this.incentiveEncarts = this.querySelector('[data-ref="incentive-encarts"]');
    this.selectedItemsList = this.querySelector('[data-ref="selected-items-list"]');
    this.subtotalPrice = this.querySelector('[data-ref="subtotal-price"]');
    this.discountAmount = this.querySelector('[data-ref="discount-amount"]');
    this.addToCartButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.atcLabel = this.querySelector('[data-ref="atc-label"]');
    this.mobilePanel = this.querySelector('[data-ref="mobile-panel"]');
    this.mobileOverlay = this.querySelector('[data-ref="mobile-overlay"]');
    this.mobilePanelHeader = this.querySelector('[data-ref="mobile-panel-header"]');
    this.mobileCloseButton = this.querySelector('[data-ref="mobile-close-button"]');
    this.mobilePanelBody = this.querySelector('[data-ref="mobile-panel-body"]');
    this.mobileItemsList = this.querySelector('[data-ref="mobile-items-list"]');
    this.mobileToggleButton = this.querySelector('[data-ref="mobile-toggle-button"]');
    this.mobileToggleIcon = this.querySelector('[data-ref="mobile-toggle-icon"]');
    this.mobileAddToCartButton = this.querySelector('[data-ref="mobile-add-to-cart-button"]');
    this.mobileAtcLabel = this.querySelector('[data-ref="mobile-atc-label"]');
    this.errorMessage = this.querySelector('[data-ref="error-message"]');
    this.mobileErrorMessage = this.querySelector('[data-ref="mobile-error-message"]');
    this.#initGroupNav();
    this.#bindGroupNavEvents();
    this.#bindGroupNavScrollSpy();
    this.#syncGroupNavStickyTop();
    this._groupNavResizeHandler = () => {
      this.#syncGroupNavStickyTop();
      this.#updateActiveGroupNavFromScroll();
    };
    window.addEventListener('resize', this._groupNavResizeHandler);
    this.scrollToProductsButtons = this.querySelectorAll('[data-ref="scroll-to-products"]');

    this.productCards = this.querySelectorAll('mix-and-match-product-card');

    this.addEventListener('mix-match:selection-change', this.#handleSelectionChange);
    this.addToCartButton?.addEventListener('click', this.#handleAddToCart);
    this.mobileAddToCartButton?.addEventListener('click', this.#handleAddToCart);
    this.mobileToggleButton?.addEventListener('click', this.#toggleMobilePanel);
    this.mobileOverlay?.addEventListener('click', this.#closeMobilePanel);
    this.mobileCloseButton?.addEventListener('click', this.#closeMobilePanel);
    this.scrollToProductsButtons.forEach((button) => {
      button.addEventListener('click', this.#handleScrollToProducts);
    });

    this.restoreSelection();
    this.initTiersProgressBars();
    this.updateUI();
    this.#setMobilePanelExpanded(false);
    this.#updateActiveGroupNavFromScroll();
  }

  onSelectionChange() {
    this.#handleSelectionChange();
  }

  disconnectedCallback() {
    this.removeEventListener('mix-match:selection-change', this.#handleSelectionChange);
    this.addToCartButton?.removeEventListener('click', this.#handleAddToCart);
    this.mobileAddToCartButton?.removeEventListener('click', this.#handleAddToCart);
    this.mobileToggleButton?.removeEventListener('click', this.#toggleMobilePanel);
    this.mobileOverlay?.removeEventListener('click', this.#closeMobilePanel);
    this.mobileCloseButton?.removeEventListener('click', this.#closeMobilePanel);
    this.querySelector('[data-ref="group-nav"]')?.removeEventListener('click', this.#handleGroupNavDelegatedClick);
    this.#unbindGroupNavScrollSpy();
    this.scrollToProductsButtons.forEach((button) => {
      button.removeEventListener('click', this.#handleScrollToProducts);
    });
    if (this._groupNavResizeHandler) {
      window.removeEventListener('resize', this._groupNavResizeHandler);
      this._groupNavResizeHandler = null;
    }
  }

  #handleScrollToProducts = (event) => {
    event.preventDefault();

    const target = this.querySelector('[data-ref="products-anchor"]');
    if (!target) return;

    const top = target.getBoundingClientRect().top + window.scrollY - this.#getCollectionScrollOffset();

    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

    if (history.replaceState && target.id) {
      history.replaceState(null, '', `#${target.id}`);
    }
  };

  #getCollectionScrollOffset() {
    const header =
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('header.header') ||
      document.querySelector('.header-wrapper');

    if (!header) return 24;

    return header.getBoundingClientRect().height + 24;
  }

  #initGroupNav() {
    const nav = this.querySelector('[data-ref="group-nav"]');
    const list = nav?.querySelector('[data-ref="group-nav-list"]');
    if (!nav || !list) return;

    const groups = [...this.querySelectorAll('.mix-match__product-group[id]')].filter((group) =>
      group.querySelector('.mix-match__product-group-title')?.textContent.trim(),
    );

    list.replaceChildren();

    if (groups.length < 2) {
      nav.hidden = true;
      this._groupNavGroups = [];
      return;
    }

    nav.hidden = false;
    this._groupNavGroups = groups;

    if (this.dataset.groupNavLabel) {
      nav.setAttribute('aria-label', this.dataset.groupNavLabel);
    }

    groups.forEach((group) => {
      const titleEl = group.querySelector('.mix-match__product-group-title');
      const label = titleEl.textContent.trim();
      const targetId = group.id;

      titleEl.setAttribute('tabindex', '-1');

      const item = document.createElement('li');
      item.className = 'mix-match__group-nav-item';

      const link = document.createElement('a');
      link.href = `#${targetId}`;
      link.className = 'mix-match__group-nav-link';
      link.dataset.collectionTarget = targetId;
      link.textContent = label;

      item.appendChild(link);
      list.appendChild(item);
    });

    this.#syncGroupNavStickyTop();
  }

  #bindGroupNavEvents() {
    const nav = this.querySelector('[data-ref="group-nav"]');
    if (!nav || nav.dataset.navBound === 'true') return;

    nav.dataset.navBound = 'true';
    nav.addEventListener('click', this.#handleGroupNavDelegatedClick);
  }

  #handleGroupNavDelegatedClick = (event) => {
    const link = event.target.closest('.mix-match__group-nav-link');
    if (!link) return;

    this.#handleCollectionNavClick(Object.assign(event, { currentTarget: link }));
  };

  #syncGroupNavStickyTop = () => {
    const nav = this.querySelector('[data-ref="group-nav"]');
    if (!nav || nav.hidden) return;

    const header =
      document.querySelector('header-component.header--sticky') ||
      document.querySelector('.section-header') ||
      document.querySelector('header.header');

    if (!header) {
      nav.style.removeProperty('top');
      return;
    }

    nav.style.top = `${header.offsetHeight}px`;
  };

  #getGroupNavScrollSpyOffset() {
    const nav = this.querySelector('[data-ref="group-nav"]');
    const header =
      document.querySelector('header-component.header--sticky') ||
      document.querySelector('.section-header') ||
      document.querySelector('header.header');

    const stickyTop = header ? header.offsetHeight : 0;
    const navHeight = nav && !nav.hidden ? nav.offsetHeight : 0;

    return stickyTop + navHeight + 12;
  }

  #setActiveGroupNavLink(targetId) {
    this.querySelectorAll('.mix-match__group-nav-link').forEach((navLink) => {
      if (navLink.dataset.collectionTarget === targetId) {
        navLink.setAttribute('aria-current', 'true');
      } else {
        navLink.removeAttribute('aria-current');
      }
    });
  }

  #updateActiveGroupNavFromScroll = () => {
    if (!this._groupNavGroups?.length) return;

    const offset = this.#getGroupNavScrollSpyOffset();
    let activeGroup = this._groupNavGroups[0];

    for (const group of this._groupNavGroups) {
      if (group.getBoundingClientRect().top <= offset) {
        activeGroup = group;
      } else {
        break;
      }
    }

    this.#setActiveGroupNavLink(activeGroup.id);
  };

  #bindGroupNavScrollSpy() {
    this.#unbindGroupNavScrollSpy();

    if (!this._groupNavGroups?.length) return;

    this._groupNavScrollHandler = () => {
      if (this._groupNavScrollRaf) return;

      this._groupNavScrollRaf = requestAnimationFrame(() => {
        this._groupNavScrollRaf = null;
        this.#updateActiveGroupNavFromScroll();
      });
    };

    window.addEventListener('scroll', this._groupNavScrollHandler, { passive: true });
  }

  #unbindGroupNavScrollSpy() {
    if (this._groupNavScrollHandler) {
      window.removeEventListener('scroll', this._groupNavScrollHandler);
      this._groupNavScrollHandler = null;
    }

    if (this._groupNavScrollRaf) {
      cancelAnimationFrame(this._groupNavScrollRaf);
      this._groupNavScrollRaf = null;
    }
  }

  #handleCollectionNavClick = (event) => {
    const link = event.currentTarget;
    const targetId = link.dataset.collectionTarget;
    const target = targetId ? document.getElementById(targetId) : null;

    if (!target) return;

    event.preventDefault();

    const top = target.getBoundingClientRect().top + window.scrollY - this.#getCollectionScrollOffset();

    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

    if (history.replaceState) {
      history.replaceState(null, '', `#${targetId}`);
    }

    this.#setActiveGroupNavLink(targetId);

    target.querySelector('.mix-match__product-group-title')?.focus({ preventScroll: true });
  };

  parseConfig() {
    this.minProducts = parseInt(this.dataset.minProducts, 10) || 2;
    this.enableBogo = this.dataset.enableBogo === 'true';
    this.quantityStep = parseInt(this.dataset.quantityStep, 10) || 1;
    this.cartLineProperty = (this.dataset.cartLineProperty || '_mix_match').trim() || '_mix_match';
    this.translations = {
      progressStart: this.dataset.progressStart || '',
      progressMax: this.dataset.progressMax || '',
      progressAddTemplate: this.dataset.progressAddTemplate || 'Ajoutez {label} pour obtenir :',
      progressAddOneProduct: this.dataset.progressAddOneProduct || '1 produit supplémentaire',
      progressAddProducts: this.dataset.progressAddProducts || '[count] produits supplémentaires',
      progressFreeProduct: this.dataset.progressFreeProduct || '1 produit offert',
      progressTierReached: this.dataset.progressTierReached || '{quantity} : {discount} appliqué',
      products: this.dataset.productsLabel || 'produits',
      addToCart: this.dataset.addToCartLabel || 'Ajouter au panier',
      addToCartWithCount: this.dataset.addToCartWithCountLabel || 'Ajouter au panier ([count])',
      minProductsError: this.dataset.minProductsError || '',
      unavailableError: this.dataset.unavailableError || '',
      subtotalLabel: this.dataset.subtotalLabel || 'Sous-total',
      discountLabel: this.dataset.discountLabel || 'Réduction',
      totalLabel: this.dataset.totalLabel || 'Total',
      removeLabel: this.dataset.removeLabel || 'Retirer',
      validateMyPack: this.dataset.validateMyPackLabel || 'Valider mon pack',
      editMyPack: this.dataset.editMyPackLabel || 'Modifier mon pack',
      tierMarkerQuantity: this.dataset.tierMarkerQuantity || '[count] produits',
      tierMarkerQuantityPlus: this.dataset.tierMarkerQuantityPlus || '[count]+ produits',
      giftFreeLabel: this.dataset.giftFreeLabel || 'Gratuit',
      giftBadgeLabel: this.dataset.giftBadgeLabel || 'Offert',
    };

    this.#loadTiersFromBlocks();
    this.#assembleBannerTierCards();
  }

  #assembleBannerTierCards() {
    const container = this.querySelector('[data-ref="tier-banner-cards"]');
    if (!container) return;

    if (container.children.length > 0) return;

    const sources = Array.from(this.querySelectorAll('[data-ref="tier-banner-card-source"]'));
    const cards = sources
      .map((source) => source.querySelector('.mix-match-banner__card'))
      .filter(Boolean)
      .sort((a, b) => {
        const minA = parseInt(a.dataset.tierMin, 10) || 0;
        const minB = parseInt(b.dataset.tierMin, 10) || 0;
        return minA - minB;
      });

    container.innerHTML = '';

    if (!cards.length) {
      container.hidden = true;
      return;
    }

    container.hidden = false;
    cards.forEach((card) => container.appendChild(card));
    sources.forEach((source) => source.remove());
  }

  #loadTiersFromBlocks() {
    let tiers = [];

    const configEl = this.querySelector('[data-ref="mix-match-config"]');
    if (configEl) {
      try {
        const config = JSON.parse(configEl.textContent.trim());
        tiers = config.tiers || [];
        this.maxTierQuantity = parseInt(config.maxTierQuantity, 10) || 0;
      } catch {
        tiers = [];
      }
    }

    if (!tiers.length) {
      tiers = Array.from(this.querySelectorAll('[data-ref="mix-match-tier-config"]'))
        .map((element) => {
          try {
            return JSON.parse(element.textContent.trim());
          } catch {
            return null;
          }
        })
        .filter(Boolean);
    }

    this.tiers = tiers.sort((a, b) => a.minQuantity - b.minQuantity);

    if (!this.maxTierQuantity) {
      const progressTiers = this.getProgressTiers();
      this.maxTierQuantity = progressTiers.length
        ? progressTiers[progressTiers.length - 1].minQuantity
        : parseInt(this.dataset.maxTierQty, 10) || 0;
    }
  }

  #handleSelectionChange = () => {
    this.rebuildSelections();
    this.persistSelection();
    this.updateUI();
  };

  getStorageKey() {
    const shop = Theme.domain || window.location.hostname;
    return `mix-match-selection:${shop}`;
  }

  persistSelection() {
    if (this.isRestoring) return;

    const items = {};

    this.selections.forEach((data) => {
      items[data.variantId] = {
        quantity: data.quantity,
      };
    });

    try {
      if (Object.keys(items).length === 0) {
        sessionStorage.removeItem(this.getStorageKey());
        return;
      }

      sessionStorage.setItem(
        this.getStorageKey(),
        JSON.stringify({
          version: 2,
          items,
        }),
      );
    } catch (error) {
      console.warn('Mix & Match: unable to persist selection', error);
    }
  }

  restoreSelection() {
    let stored = null;

    try {
      stored = JSON.parse(sessionStorage.getItem(this.getStorageKey()) || 'null');
    } catch {
      sessionStorage.removeItem(this.getStorageKey());
      return;
    }

    if (!stored?.items || typeof stored.items !== 'object') return;

    this.isRestoring = true;

    Object.entries(stored.items).forEach(([key, state]) => {
      const variantId = state.variantId || key;
      const card = this.querySelector(`mix-and-match-product-card[data-variant-id="${variantId}"]`);
      if (!card) return;
      card.restoreFromState({ variantId, quantity: state.quantity });
    });

    this.isRestoring = false;
    this.rebuildSelections();
  }

  clearPersistedSelection() {
    try {
      sessionStorage.removeItem(this.getStorageKey());
    } catch (error) {
      console.warn('Mix & Match: unable to clear persisted selection', error);
    }
  }

  rebuildSelections() {
    this.selections.clear();

    this.productCards.forEach((card) => {
      const data = card.getSelectionData();
      if (!data) return;
      this.selections.set(data.variantId, data);
    });
  }

  getTotalCount() {
    let count = 0;
    this.selections.forEach((item) => {
      count += item.quantity;
    });
    return count;
  }

  getSubtotal() {
    let subtotal = 0;
    this.selections.forEach((item) => {
      subtotal += item.price * item.quantity;
    });
    return subtotal;
  }

  getActiveTier(count) {
    if (!this.tiers.length) return null;

    const matching = this.tiers.filter((tier) => {
      const maxQty = tier.maxQuantity > 0 ? tier.maxQuantity : Infinity;
      return count >= tier.minQuantity && count <= maxQty;
    });

    if (!matching.length) return null;

    return matching.sort((a, b) => b.discountPercentage - a.discountPercentage)[0];
  }

  getActiveGiftProduct(count) {
    return this.getActiveTier(count)?.giftProduct || null;
  }

  shouldIncludeGift(count) {
    const gift = this.getActiveGiftProduct(count);
    if (!gift?.variantId) return false;

    return !this.selections.has(String(gift.variantId));
  }

  getNextTier(count) {
    return this.tiers
      .filter((tier) => tier.discountPercentage > 0 && count < tier.minQuantity)
      .sort((a, b) => a.minQuantity - b.minQuantity)[0];
  }

  #boldDiscountInMessage(message) {
    return message.replace(/(-\d+%)/g, '<strong>$1</strong>');
  }

  #formatTierQuantityLabel(minQuantity, maxQuantity) {
    const template =
      maxQuantity === 0
        ? this.translations.tierMarkerQuantityPlus
        : this.translations.tierMarkerQuantity;

    return template.replace(/\[count\]/g, String(minQuantity));
  }

  #formatProgressAddLabel(needed) {
    return needed === 1
      ? this.translations.progressAddOneProduct
      : this.translations.progressAddProducts.replace(/\[count\]/g, String(needed));
  }

  #formatProgressNextMessageHtml(count, tier) {
    const needed = Math.max(0, tier.minQuantity - count);
    const label = this.#formatProgressAddLabel(needed);
    const messageText = this.translations.progressAddTemplate.replace(
      '{label}',
      `<strong>${label}</strong>`,
    );

    let messageHtml = '';
    if (tier.discountPercentage > 0) {
      messageHtml = `${messageText} <strong class="mix-match-tier-progress__message-discount">-${tier.discountPercentage}%</strong>`;
    } else if (this.enableBogo) {
      messageHtml = `${messageText} <strong class="mix-match-tier-progress__message-discount">${this.translations.progressFreeProduct}</strong>`;
    }
    return messageHtml;
  }

  #formatTierReachedMessagePlain(tier) {
    const quantity = this.#formatTierQuantityLabel(tier.minQuantity, tier.maxQuantity || 0);

    return this.translations.progressTierReached
      .replace('{quantity}', quantity)
      .replace('{discount}', `-${tier.discountPercentage}%`);
  }

  getIncentiveEncarts(count) {
    const unreachedTiers = this.tiers
      .filter((tier) => count < tier.minQuantity)
      .sort((a, b) => a.minQuantity - b.minQuantity);

    if (!unreachedTiers.length) return [];

    if (count === 0) {
      const encarts = [
        {
          type: 'start',
          message: this.#boldDiscountInMessage(this.translations.progressStart),
          icon: 'start',
        },
      ];

      const nextTier =
        unreachedTiers.find((tier) => tier.discountPercentage > 0) || unreachedTiers[0];

      if (nextTier) {
        encarts.push({
          type: 'tier',
          message: this.#formatProgressNextMessageHtml(count, nextTier),
          icon: 'tier',
        });
      }

      return encarts.slice(0, 2);
    }

    const nextTier = unreachedTiers[0];

    return [
      {
        type: 'tier',
        message: this.#formatProgressNextMessageHtml(count, nextTier),
        icon: 'tier',
      },
    ];
  }

  #getIncentiveIcon(type) {
    if (type === 'start') {
      return `
        <svg class="mix-match-summary__incentive-icon" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <circle cx="20" cy="22" r="10" fill="#f5ebe0"/>
          <circle cx="14" cy="30" r="4" fill="#fff" stroke="#f87c58" stroke-width="1.5"/>
          <circle cx="24" cy="31" r="3" fill="#fff" stroke="#f87c58" stroke-width="1.5"/>
          <circle cx="20" cy="12" r="6" fill="#f87c58" opacity="0.85"/>
        </svg>
      `;
    }

    return `
      <svg class="mix-match-summary__incentive-icon" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <ellipse cx="20" cy="30" rx="12" ry="4" fill="#f5ebe0"/>
        <rect x="13" y="14" width="14" height="16" rx="4" fill="#fff" stroke="#482e0e" stroke-width="1.5"/>
        <rect x="15" y="10" width="10" height="5" rx="2" fill="#f87c58"/>
        <circle cx="17" cy="22" r="2" fill="#f5ebe0"/>
        <circle cx="23" cy="24" r="1.5" fill="#f5ebe0"/>
      </svg>
    `;
  }

  #renderIncentiveEncart(encart) {
    return `
      <div class="mix-match-summary__incentive">
        <div class="mix-match-summary__incentive-icon-wrap">
          ${this.#getIncentiveIcon(encart.icon || 'tier')}
        </div>
        <p class="mix-match-summary__incentive-text">${encart.message}</p>
      </div>
    `;
  }

  #renderIncentiveEncartsHtml(count) {
    const encarts = this.getIncentiveEncarts(count);

    if (!encarts.length) return '';

    return encarts.map((encart) => this.#renderIncentiveEncart(encart)).join('');
  }

  updateIncentiveEncarts(count) {
    const html = this.#renderIncentiveEncartsHtml(count);

    [this.incentiveEncarts].forEach((container) => {
      if (!container) return;

      container.innerHTML = html;
      container.hidden = !html;
    });
  }

  getProgressMessage(count) {
    const activeTier = this.getActiveTier(count);
    const nextTier = this.getNextTier(count);

    if (activeTier?.discountPercentage > 0 && !nextTier) {
      return this.translations.progressMax.replace('[discount]', String(activeTier.discountPercentage));
    }

    if (nextTier) {
      const needed = nextTier.minQuantity - count;
      const label = this.#formatProgressAddLabel(needed);

      return `${this.translations.progressAddTemplate.replace('{label}', label)} -${nextTier.discountPercentage}%`;
    }

    if (count === 0) {
      return this.translations.progressStart;
    }

    if (activeTier?.discountPercentage > 0) {
      return this.#formatTierReachedMessagePlain(activeTier);
    }

    return this.translations.progressStart;
  }

  calculatePricing() {
    const count = this.getTotalCount();
    const subtotal = this.getSubtotal();
    const tier = this.getActiveTier(count);
    const tierDiscountPercentage = tier?.discountPercentage || 0;
    let discountAmount = Math.round((subtotal * tierDiscountPercentage) / 100);
    let total = subtotal - discountAmount;

    if (this.enableBogo && total > 0) {
      total = Math.round(total / 2);
      discountAmount = subtotal - total;
    }

    const discountPercentage =
      subtotal > 0 ? Math.round((discountAmount / subtotal) * 100) : tierDiscountPercentage;

    return { count, subtotal, discountAmount, total, discountPercentage, tier };
  }

  #setPriceElement(element, cents) {
    if (!element) return;
    element.innerHTML = cents > 0 ? formatMoney(cents) : '—';
  }

  #renderAtcLabel(canCheckout, count, subtotal, total, discountAmount) {
    if (!canCheckout || count < 1) {
      return this.translations.addToCart;
    }

    const totalPrice = formatMoney(total);

    if (discountAmount > 0) {
      const comparePrice = formatMoney(subtotal);
      return `${this.translations.addToCart} - ${totalPrice} <s class="mix-match-summary__atc-compare">${comparePrice}</s>`;
    }

    return `${this.translations.addToCart} - ${totalPrice}`;
  }

  #updateAtcLabels(canCheckout, count, subtotal, total, discountAmount) {
    const labelHtml = this.#renderAtcLabel(canCheckout, count, subtotal, total, discountAmount);

    [this.atcLabel, this.mobileAtcLabel].forEach((label) => {
      if (!label) return;
      label.innerHTML = labelHtml;
    });
  }

  #shouldShowVariantTitle(variantTitle) {
    return Boolean(variantTitle && variantTitle !== 'Default Title');
  }

  #renderSummaryItemInfo(item) {
    const variantMarkup = this.#shouldShowVariantTitle(item.variantTitle)
      ? `<span class="mix-match-summary__item-variant">${item.variantTitle}</span>`
      : '';

    return `
      <span class="mix-match-summary__item-title">${item.productTitle}</span>
      ${variantMarkup}
    `;
  }

  #renderSummaryGiftItem(gift) {
    return `
      <li class="mix-match-summary__item" data-mix-match-item="gift" data-variant-id="${gift.variantId}">
        <img src="${gift.image}" alt="" class="mix-match-summary__item-image" width="48" height="48" loading="lazy">
        <div class="mix-match-summary__item-info">
          ${this.#renderSummaryItemInfo(gift)}
          <div class="mix-match-summary__item-meta">
            <span class="mix-match-summary__item-price mix-match-summary__item-price--free">${this.translations.giftFreeLabel}</span>
          </div>
        </div>
      </li>
    `;
  }

  #renderSummaryItemMeta(item, discountPercentage = 0) {
    const baseLinePrice = item.price * item.quantity;
    const tierDiscountPercentage = this.enableBogo
      ? this.getActiveTier(this.getTotalCount())?.discountPercentage || 0
      : discountPercentage;

    let finalLinePrice =
      tierDiscountPercentage > 0
        ? Math.round((baseLinePrice * (100 - tierDiscountPercentage)) / 100)
        : baseLinePrice;

    if (this.enableBogo) {
      finalLinePrice = Math.round(finalLinePrice / 2);
    }

    let compareLinePrice = null;
    if (!Number.isNaN(item.compareAtPrice) && item.compareAtPrice > item.price) {
      compareLinePrice = item.compareAtPrice * item.quantity;
    } else if (finalLinePrice < baseLinePrice) {
      compareLinePrice = baseLinePrice;
    }

    if (compareLinePrice != null && compareLinePrice <= finalLinePrice) {
      compareLinePrice = null;
    }

    const priceMarkup =
      compareLinePrice != null
        ? `<span class="mix-match-summary__item-prices">
             <span class="mix-match-summary__item-price">${formatMoney(finalLinePrice)}</span>
             <s class="mix-match-summary__item-price--compare">${formatMoney(compareLinePrice)}</s>
           </span>`
        : `<span class="mix-match-summary__item-price">${formatMoney(finalLinePrice)}</span>`;

    return `
      <div class="mix-match-summary__item-meta">
        ${priceMarkup}
        ${this.#renderSummaryQuantitySelector(item)}
      </div>
    `;
  }

  #getIconClass(iconName) {
    const sampleIcon = this.querySelector('.icon');
    const baseClass = sampleIcon?.className.match(/material-symbols-\w+/)?.[0] || 'material-symbols-rounded';

    return `${baseClass} notranslate icon icon--${iconName}`;
  }

  #renderSummaryQuantitySelector(item) {
    const name = `mix-match-summary-qty-${item.variantId}`;

    return `
      <quantity-selector class="quantity-selector mix-match-summary__item-qty-selector">
        <button type="button" name="minus" data-ref="mix-match-summary-qty-button">
          <span class="${this.#getIconClass('remove')}" translate="no">remove</span>
        </button>
        <input
          type="number"
          name="${name}"
          value="${item.quantity}"
          min="0"
          step="${this.quantityStep}"
          data-ref="mix-match-summary-qty-input"
          data-variant-id="${item.variantId}"
        >
        <button type="button" name="plus" data-ref="mix-match-summary-qty-button">
          <span class="${this.#getIconClass('add')}" translate="no">add</span>
        </button>
      </quantity-selector>
    `;
  }

  #bindSummaryItemControls(container) {
    if (!container) return;

    container.querySelectorAll('[data-ref="mix-match-summary-qty-input"]').forEach((input) => {
      input.addEventListener('change', () => {
        const variantId = input.dataset.variantId;
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.#handleSummaryItemQuantityChange(variantId, quantity);
      });
    });

    container.querySelectorAll('.mix-match-summary__item-remove').forEach((button) => {
      button.addEventListener('click', () => {
        this.#handleSummaryItemRemove(button.dataset.variantId);
      });
    });
  }

  #handleSummaryItemQuantityChange(variantId, quantity) {
    const card = this.querySelector(`mix-and-match-product-card[data-variant-id="${variantId}"]`);
    if (!card) return;

    if (quantity < 1) {
      card.clearSelection();
      return;
    }

    card.setQuantity(quantity);
    card.dispatchSelectionChange();
  }

  getDiscountTiers() {
    return [...this.tiers]
      .filter((tier) => tier.discountPercentage > 0)
      .sort((a, b) => a.minQuantity - b.minQuantity);
  }

  getProgressTiers() {
    return [...this.tiers].sort((a, b) => a.minQuantity - b.minQuantity);
  }

  getMaxTierQuantity() {
    const progressTiers = this.getProgressTiers();
    if (progressTiers.length) {
      return progressTiers[progressTiers.length - 1].minQuantity;
    }

    if (this.maxTierQuantity > 0) {
      return this.maxTierQuantity;
    }

    return 0;
  }

  #renderTierProgressMessage(count) {
    const progressTiers = this.getProgressTiers();
    if (!progressTiers.length) return '';

    const nextDiscountTier = this.getNextTier(count);
    const activeTier = this.getActiveTier(count);

    if (activeTier?.discountPercentage > 0 && !nextDiscountTier) {
      const message = this.translations.progressMax.replace(
        '[discount]',
        String(activeTier.discountPercentage),
      );

      return `<p class="mix-match-tier-progress__message mix-match-tier-progress__message--complete">${this.#boldDiscountInMessage(message)}</p>`;
    }

    if (nextDiscountTier) {
      const needed = Math.max(0, nextDiscountTier.minQuantity - count);
      const label = this.#formatProgressAddLabel(needed);
      const messageText = this.translations.progressAddTemplate.replace(
        '{label}',
        `<strong>${label}</strong>`,
      );

      return `
        <p class="mix-match-tier-progress__message">
          <span class="mix-match-tier-progress__message-text">${messageText}</span>
          <strong class="mix-match-tier-progress__message-discount">-${nextDiscountTier.discountPercentage}%</strong>
        </p>
      `;
    }

    if (count === 0) {
      return `<p class="mix-match-tier-progress__message mix-match-tier-progress__message--complete">${this.translations.progressStart}</p>`;
    }

    return '';
  }

  ensureTierSegments(container) {
    const segmentsContainer = container.querySelector('[data-ref="tier-progress-segments"]');
    if (!segmentsContainer) return;

    const progressTiers = this.getProgressTiers();
    if (!progressTiers.length) return;

    if (!segmentsContainer.querySelector('[data-ref="tier-segment"]')) {
      segmentsContainer.innerHTML = progressTiers
        .map(
          (tier) => `
            <span
              class="mix-match-tier-progress__segment"
              data-ref="tier-segment"
              data-tier-min="${tier.minQuantity}"
              data-tier-discount="${tier.discountPercentage}"
            ></span>
          `,
        )
        .join('');
    }
  }

  initTiersProgressBars() {
    const progressTiers = this.getProgressTiers();

    this.querySelectorAll('[data-ref="tier-progress"]').forEach((container) => {
      if (!progressTiers.length) {
        container.hidden = true;
        return;
      }

      container.hidden = false;
      container.dataset.maxQty = String(this.getMaxTierQuantity());
      container.dataset.tierCount = String(progressTiers.length);
      container.setAttribute('aria-valuemax', String(progressTiers.length));
      this.ensureTierSegments(container);
    });
  }

  updateTiersProgressBar(count) {
    const progressTiers = this.getProgressTiers();

    this.querySelectorAll('[data-ref="tier-progress"]').forEach((container) => {
      if (!progressTiers.length) {
        container.hidden = true;
        return;
      }

      container.hidden = false;

      const messageEl = container.querySelector('[data-ref="tier-progress-message"]');
      if (messageEl) {
        messageEl.innerHTML = this.#renderTierProgressMessage(count);
      }

      this.ensureTierSegments(container);

      let filledCount = 0;

      container.querySelectorAll('[data-ref="tier-segment"]').forEach((segment) => {
        const min = parseInt(segment.dataset.tierMin, 10);
        const reached = count >= min;

        segment.classList.toggle('mix-match-tier-progress__segment--filled', reached);

        if (reached) {
          filledCount += 1;
        }
      });

      container.setAttribute('aria-valuenow', String(filledCount));
    });
  }

  updateUI() {
    const { count, subtotal, discountAmount, total, discountPercentage } = this.calculatePricing();
    const canCheckout = count > 0;

    if (this.summaryCount) this.summaryCount.textContent = String(count);

    this.updateIncentiveEncarts(count);
    this.updateTiersProgressBar(count);

    this.#setPriceElement(this.subtotalPrice, count > 0 ? subtotal : 0);

    if (this.discountAmount) {
      this.discountAmount.innerHTML =
        discountAmount > 0 ? `-${formatMoney(discountAmount)} (${discountPercentage}%)` : '—';
    }

    this.updateSelectedItemsList();
    this.updateAddToCartButtons(canCheckout, { count, subtotal, total, discountAmount });
    this.hideError();
  }

  updateSelectedItemsList() {
    const { count, discountPercentage } = this.calculatePricing();

    [this.selectedItemsList, this.mobileItemsList].forEach((list) => {
      if (!list) return;

      list.innerHTML = '';

      this.selections.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'mix-match-summary__item';
        li.dataset.mixMatchItem = 'product';
        li.dataset.variantId = item.variantId;
        li.innerHTML = `
        <img src="${item.image}" alt="" class="mix-match-summary__item-image" width="48" height="48" loading="lazy">
        <div class="mix-match-summary__item-info">
          ${this.#renderSummaryItemInfo(item)}
          ${this.#renderSummaryItemMeta(item, discountPercentage)}
        </div>
        <button type="button" class="mix-match-summary__item-remove" data-variant-id="${item.variantId}" aria-label="${this.translations.removeLabel}">
          ×
        </button>
      `;
        list.appendChild(li);
      });

      const gift = this.shouldIncludeGift(count) ? this.getActiveGiftProduct(count) : null;

      if (gift) {
        list.insertAdjacentHTML('beforeend', this.#renderSummaryGiftItem(gift));
      }

      this.#bindSummaryItemControls(list);
    });
  }

  #handleSummaryItemRemove(variantId) {
    const card = this.querySelector(`mix-and-match-product-card[data-variant-id="${variantId}"]`);
    card?.clearSelection();
    this.rebuildSelections();
    this.persistSelection();
    this.updateUI();
    card?.dispatchSelectionChange();
  }

  updateAddToCartButtons(canCheckout, pricing) {
    const { count, subtotal, total, discountAmount } = pricing;

    [this.addToCartButton, this.mobileAddToCartButton].forEach((button) => {
      if (!button) return;

      button.disabled = !canCheckout || this.isSubmitting;
      button.setAttribute('aria-disabled', String(!canCheckout || this.isSubmitting));
      button.classList.toggle('is-loading', this.isSubmitting);
    });

    this.#updateAtcLabels(canCheckout, count, subtotal, total, discountAmount);
  }

  #isMobilePanelExpanded() {
    return this.summaryPanel?.classList.contains('mix-match-summary--expanded') ?? false;
  }

  #updateMobileToggleUI(isExpanded) {
    if (!this.mobileToggleButton) return;

    this.mobileToggleButton.classList.toggle('button--primary', !isExpanded);
    this.mobileToggleButton.classList.toggle('button--secondary', isExpanded);

    if (this.mobileToggleIcon) {
      const iconName = isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_up';
      this.mobileToggleIcon.textContent = iconName;
      this.mobileToggleIcon.classList.remove('icon--keyboard_arrow_up', 'icon--keyboard_arrow_down');
      this.mobileToggleIcon.classList.add(`icon--${iconName}`);
    }
  }

  #setMobilePanelExpanded(isExpanded) {
    this.summaryPanel?.classList.toggle('mix-match-summary--expanded', isExpanded);

    this.mobileOverlay?.setAttribute('aria-hidden', String(!isExpanded));
    this.mobilePanelHeader?.setAttribute('aria-hidden', String(!isExpanded));
    this.mobilePanelBody?.setAttribute('aria-hidden', String(!isExpanded));
    this.mobileAddToCartButton?.setAttribute('aria-hidden', String(!isExpanded));

    if (isExpanded) {
      this.mobileOverlay?.removeAttribute('tabindex');
    } else {
      this.mobileOverlay?.setAttribute('tabindex', '-1');
    }

    this.mobileToggleButton?.setAttribute('aria-expanded', String(isExpanded));
    this.#updateMobileToggleUI(isExpanded);
  }

  #closeMobilePanel = () => {
    this.#setMobilePanelExpanded(false);
  };

  #toggleMobilePanel = () => {
    this.#setMobilePanelExpanded(!this.#isMobilePanelExpanded());
  };

  showError(message) {
    [this.errorMessage, this.mobileErrorMessage].forEach((element) => {
      if (!element) return;

      element.textContent = message;
      element.classList.remove('hidden');
    });
  }

  hideError() {
    [this.errorMessage, this.mobileErrorMessage].forEach((element) => {
      if (!element) return;

      element.classList.add('hidden');
      element.textContent = '';
    });
  }

  #handleAddToCart = async (event) => {
    event.preventDefault();

    this.rebuildSelections();
    const { count } = this.calculatePricing();

    if (count < 1) {
      this.showError(this.translations.minProductsError.replace('[count]', '1'));
      return;
    }

    const items = [];
    const campaignProperty = this.cartLineProperty || '_mix_match';

    this.selections.forEach((item) => {
      items.push({
        id: parseInt(item.variantId, 10),
        quantity: item.quantity,
        properties: {
          [campaignProperty]: 'true',
        },
      });
    });

    if (this.shouldIncludeGift(count)) {
      const gift = this.getActiveGiftProduct(count);

      items.push({
        id: parseInt(gift.variantId, 10),
        quantity: 1,
        properties: {
          [campaignProperty]: 'true',
        },
      });
    }

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      sections.push(cartDrawer.dataset.sectionId);
    }

    this.isSubmitting = true;
    this.updateAddToCartButtons(false, this.calculatePricing());

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || this.translations.unavailableError);
      }

      this.productCards.forEach((card) => card.clearSelection());
      this.rebuildSelections();
      this.clearPersistedSelection();
      this.updateUI();
      this.#setMobilePanelExpanded(false);

      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
      const itemsAdded = items.reduce((sum, item) => sum + item.quantity, 0);
      const newItemCount = currentItemCount != null ? currentItemCount + itemsAdded : undefined;

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'mix-and-match',
            sections: data.sections,
          },
        ),
      );
    } catch (error) {
      this.showError(error.message || this.translations.unavailableError);
    } finally {
      this.isSubmitting = false;
      this.updateUI();
    }
  };
}

if (!customElements.get('mix-and-match-product-card')) {
  customElements.define('mix-and-match-product-card', MixAndMatchProductCard);
}

if (!customElements.get('mix-and-match-component')) {
  customElements.define('mix-and-match-component', MixAndMatchComponent);
}
